import React from "react";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { ReactComponent as SearchIcon } from "../../icons/search-2.svg";
import { ReactComponent as CancelIcon } from "../../icons/close.svg";
import tw from "twin.macro";
import { Icon } from "assets/Icon";
import {
	CancelButton,
	FormStyled,
	Results,
	SearchBoxButtons,
	SearchBoxContainer,
	SearchBoxInputContainer,
	SearchBoxInputRFQContainer,
	SearchBoxInputRFQStyled,
	SearchBoxInputStyled,
	SearchBoxSubmit,
	SearchIconContainer,
	SectorsStyled,
} from "./styled";

// Types
interface FormValues {
	keyword: string;
	search: string;
	sector: string | string[];
}

interface SearchBoxComponentProps {
	className?: string;
	companyId?: number;
	error?: string;
	isCompanyList?: boolean;
	keywordFilter?: boolean;
	multiSector?: boolean;
	placeholder?: string;
	resultsCount?: number;
	searchInput?: boolean;
	searchProject?: boolean;
	sectorFilter?: boolean;
	showProjectsCount?: boolean; // Note: keeping original typo for consistency
	touched?: boolean;
	whiteInput?: boolean;
	handleFormSubmit: (values: FormValues) => void;
}

const initialValues = { search: "", sector: "", keyword: "" };

export const SearchBox: React.FC<SearchBoxComponentProps> = ({
	handleFormSubmit,
	searchInput,
	sectorFilter,
	keywordFilter,
	searchProject,
	showProjectsCount,
	multiSector,
	className,
	whiteInput,
	resultsCount = 0,
	isCompanyList,
	placeholder,
	touched,
	error,
	companyId,
}) => {
	const selectStyles = () => ({
		control: (styles: any, { isDisabled }: { isDisabled: boolean }) => ({
			...styles,
			height: "48px",
			maxHeight: "48px",
			padding: "0 8px",
			borderRadius: "6px",
			...(touched && error
				? tw`focus-within:ring-4 focus-within:!border-utility-error-300 !ring-ring-red-primary`
				: tw`focus-within:ring-4 focus-within:!border-utility-brand-300 !ring-ring-blue-primary border border-[#D0D5DD] shadow-sm`),
		}),
	});

	const validationSchema = Yup.object({
		keyword: Yup.string().min(2, "Keyword must be at least 2 characters"),
		search: Yup.string(),
		sector: Yup.mixed(),
	});

	return (
		<Formik<FormValues>
			enableReinitialize
			initialValues={initialValues}
			onSubmit={(values: FormValues) => {
				handleFormSubmit(values);
			}}
			validationSchema={validationSchema}
		>
			{(formikProps: FormikProps<FormValues>) => {
				const { setFieldValue, values, handleSubmit } = formikProps;

				const removeKeyword = (): void => {
					setFieldValue("keyword", "", false);
					handleFormSubmit({ ...values, keyword: "" });
				};

				return (
					<SearchBoxContainer className={className}>
						{resultsCount > 0 && (
							<Results>
								{resultsCount} project{resultsCount > 1 ? "s" : ""} found
							</Results>
						)}

						<FormStyled>
							{searchInput && (
								<SearchBoxInputContainer>
									{
										// @ts-ignore
										<SearchBoxInputStyled
											type="text"
											name="search"
											placeholder="Search by name"
											white={whiteInput}
										/>
									}
									<Icon
										icon="search"
										style={{ width: "20px", height: "20px" }}
										className="absolute left-3 top-1/2 -translate-y-1/2"
									/>
									<SearchBoxButtons>
										<SearchBoxSubmit
											type="submit"
											name="submit"
										/>
									</SearchBoxButtons>
								</SearchBoxInputContainer>
							)}

							{keywordFilter && (
								<SearchBoxInputContainer>
									{
										// @ts-ignore
										<SearchBoxInputStyled
											testId="search-box-keyword"
											type="text"
											name="keyword"
											placeholder={placeholder || "Search by keyword"}
											white={whiteInput}
											isNoPadding={isCompanyList}
										/>
									}
									{!isCompanyList && (
										<Icon
											icon="search"
											style={{ width: "20px", height: "20px" }}
											className="absolute left-3 top-1/2 -translate-y-1/2"
										/>
									)}
									<SearchBoxButtons>
										{isCompanyList ? (
											<>
												{values?.keyword && (
													<CancelButton
														type="button"
														onClick={removeKeyword}
													>
														<CancelIcon />
													</CancelButton>
												)}
												<SearchBoxSubmit
													disabled={!values?.keyword}
													type="submit"
													name="submit"
												>
													<SearchIcon />
												</SearchBoxSubmit>
											</>
										) : (
											<>
												{values?.keyword && (
													<CancelButton
														type="button"
														onClick={removeKeyword}
													>
														<CancelIcon />
													</CancelButton>
												)}
												<SearchBoxSubmit
													disabled={!values?.keyword}
													type="submit"
													name="submit"
												/>
											</>
										)}
									</SearchBoxButtons>
								</SearchBoxInputContainer>
							)}

							{searchProject && (
								<SearchBoxInputRFQContainer>
									{
										//@ts-ignore
										<SearchBoxInputRFQStyled
											type="text"
											name="keyword"
											placeholder="Search"
										/>
									}
									<SearchIconContainer>
										<SearchIcon />
									</SearchIconContainer>
								</SearchBoxInputRFQContainer>
							)}

							{sectorFilter && (
								<SectorsStyled
									testId="filter-sector"
									isSearchBox
									styles={selectStyles()}
									noLabel
									name="sector"
									setFieldValue={setFieldValue}
									defaultValue={initialValues.sector}
									values={values}
									singleValue={!multiSector}
									placeholder={`Filter by sector${multiSector ? "s" : ""}`}
									clearable
									className="test-filter-sector"
									showProjectsCount={showProjectsCount}
									companyId={companyId}
									onChange={(option: any) => {
										if (multiSector) {
											const valuesArray: string[] = option
												? option.map((opt: any) => opt.value)
												: [];
											setFieldValue("sector", valuesArray);
										} else {
											setFieldValue("sector", option?.value || "");
										}
										handleSubmit();
									}}
								/>
							)}
						</FormStyled>
					</SearchBoxContainer>
				);
			}}
		</Formik>
	);
};
